import { humanize } from '@ideon-api/ideon-ui'
import type { AuthPrivilegedAccessEntry as Entry } from '@ideon-api/sdk-internal'
import { US_CODES, countStates } from '../pages/account-detail/CoverageLine'
import { groupKeyFor } from './product-lines'

export interface PrivilegedGroup {
  key: string
  title: string
  noun: string
  entries: Entry[]
}

export { groupKeyFor }

export type DataQualityFlag = 'malformed_name' | 'case_variant_duplicate' | 'ignored_wildcard'

export const GROUPS = [
  { key: 'medical', title: 'Medical · Small Group', noun: 'medical' },
  { key: 'level_funded', title: 'Medical · Level-Funded', noun: 'level-funded' },
  { key: 'ancillary', title: 'Ancillary · Small Group', noun: 'ancillary' },
] as const

export function groupEntries(entries: readonly Entry[]): PrivilegedGroup[] {
  const groups: PrivilegedGroup[] = GROUPS.map((g) => ({ ...g, entries: [] }))
  for (const entry of entries) {
    const key = groupKeyFor(entry.productLine)
    let group = groups.find((g) => g.key === key)
    if (!group) {
      group = { key, title: humanize(entry.productLine), noun: humanize(entry.productLine).toLowerCase(), entries: [] }
      groups.push(group)
    }
    group.entries.push(entry)
  }
  return groups
}

const normalized = (states: readonly string[]) => states.map((s) => s.trim().toUpperCase())

export function neverGranted(entry: Entry): string[] {
  const seen = new Set([...normalized(entry.effectiveStates), ...normalized(entry.negatedStates)])
  return US_CODES.filter((c) => !seen.has(c))
}

export function bandCounts(entry: Entry): { granted: number; noAccess: number } {
  const effective = new Set(normalized(entry.effectiveStates))
  return {
    granted: countStates(entry.effectiveStates),
    // Complement of the effective set, so the two bands always partition the 51 codes.
    noAccess: US_CODES.length - US_CODES.filter((c) => effective.has(c)).length,
  }
}

// Same normalisation as PrivilegedCarriers::Catalog#case_variant_pairs (downcase.squish).
const carrierKey = (c: string) => c.toLowerCase().trim().replace(/\s+/g, ' ')

const MALFORMED = /["“”]/

export function dataQualityFlags(entries: readonly Entry[]): Map<Entry, DataQualityFlag[]> {
  const spellings = new Map<string, Set<string>>()
  for (const { productLine, carrier } of entries) {
    const k = `${productLine}\u0000${carrierKey(carrier)}`
    spellings.set(k, (spellings.get(k) ?? new Set()).add(carrier))
  }
  return new Map(
    entries.map((e) => {
      const flags: DataQualityFlag[] = []
      if (MALFORMED.test(e.carrier)) flags.push('malformed_name')
      if ((spellings.get(`${e.productLine}\u0000${carrierKey(e.carrier)}`)?.size ?? 0) > 1) flags.push('case_variant_duplicate')
      if ([e.wildcardStates, e.grantedStates, e.negatedStates].some((codes) => codes?.includes('*'))) flags.push('ignored_wildcard')
      return [e, flags]
    })
  )
}

// The calculator expands "*" for ancillary lines, so one that reaches the UI was ignored by the policy.
export const displayCodes = (codes: readonly string[]): string[] =>
  codes.map((c) => (c === '*' ? '* (ignored: list the states)' : c))
